'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from '@shared/all/domains/posts';
import { useUserIdentity } from '@shared/utils/context/UserContext';
import { formatNumbers } from '@shared/utils/helpers/formatNumbers';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { F } from '@shared/utils/icons';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import { useAuthModals } from '../../widgets/WidgetsContext';
import FactiiiModal from '../factiiis/FactiiiModal';
type Props = {
  post: Post;
  nsfw: boolean;
  politics: boolean;
  troll?: boolean;
  setNsfw?: (arg0: boolean) => void;
  setPolitics?: (arg0: boolean) => void;
  setTroll?: (arg0: boolean) => void;
  minified?: boolean;
  uniqueId?: string;
  // Hide the numeric count — used where the count is already shown nearby
  // (e.g. next to the PostFactiiisCount avatars in the post head).
  hideCount?: boolean;
};

// The "F" action button. Shows the factiii count (display-only) and opens
// the FactiiiModal to attach more. The count no longer toggles the inline
// breakdown — PostFactiiisCount renders that on its own now.
const PostFactiiiButton = props => {
  const $ = _c(36);
  const currentUser = useUserIdentity();
  const {
    openAuthContinueModal
  } = useAuthModals();
  const {
    refreshPost
  } = usePostsShared(client, props.uniqueId);
  const [factiiiModalOpen, setFactiiiModalOpen] = useState(false);
  const factiiiCount = props.post.postFactiiisCount?.length ?? 0;
  const t0 = `flex items-center justify-center gap-1 rounded-md transition-all duration-200 hover:scale-[1.05] hover:bg-background-light active:scale-[0.95] ${props.minified ? "px-1.5 py-1" : "px-2 py-1"}`;
  let t1;
  if ($[0] !== currentUser.isAuthenticated || $[1] !== openAuthContinueModal) {
    t1 = e => {
      e.stopPropagation();
      if (!currentUser.isAuthenticated) {
        return openAuthContinueModal();
      }
      setFactiiiModalOpen(true);
    };
    $[0] = currentUser.isAuthenticated;
    $[1] = openAuthContinueModal;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = props.minified ? 16 : 26;
  let t3;
  if ($[3] !== t2) {
    t3 = <F height={t2} color="rgb(var(--text-secondary-color))" />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== props.minified) {
    t4 = props.minified ? {
      width: 11,
      height: 11
    } : {
      width: 14,
      height: 14
    };
    $[5] = props.minified;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = props.minified ? 8 : 10;
  let t6;
  if ($[7] !== t5) {
    t6 = <Plus size={t5} strokeWidth={3.5} />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = <span className="absolute -bottom-1 -right-1.5 flex items-center justify-center rounded-full bg-background-light text-textPrimary shadow ring-2 ring-background-primary" style={t4}>{t6}</span>;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t3 || $[13] !== t7) {
    t8 = <span className="relative flex items-center justify-center">{t3}{t7}</span>;
    $[12] = t3;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== factiiiCount || $[16] !== props.hideCount) {
    t9 = !props.hideCount && factiiiCount > 0 && <span className="text-xs text-textSecondary">{formatNumbers(factiiiCount)}</span>;
    $[15] = factiiiCount;
    $[16] = props.hideCount;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t0 || $[19] !== t1 || $[20] !== t8 || $[21] !== t9) {
    t10 = <button data-id="post-factiii-add" title="Add Factiii" className={t0} onClick={t1}>{t8}{t9}</button>;
    $[18] = t0;
    $[19] = t1;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== factiiiModalOpen || $[24] !== props.nsfw || $[25] !== props.politics || $[26] !== props.post || $[27] !== props.setNsfw || $[28] !== props.setPolitics || $[29] !== props.setTroll || $[30] !== props.troll || $[31] !== refreshPost) {
    t11 = factiiiModalOpen && props.setNsfw && props.setPolitics && props.setTroll && <div onClick={_temp}><FactiiiModal closeHandler={() => setFactiiiModalOpen(false)} post={props.post} refreshPost={id => refreshPost(id, props.post.isPrivate)} nsfw={props.nsfw} setNsfw={props.setNsfw} politics={props.politics} setPolitics={props.setPolitics} troll={Boolean(props.troll)} setTroll={props.setTroll} /></div>;
    $[23] = factiiiModalOpen;
    $[24] = props.nsfw;
    $[25] = props.politics;
    $[26] = props.post;
    $[27] = props.setNsfw;
    $[28] = props.setPolitics;
    $[29] = props.setTroll;
    $[30] = props.troll;
    $[31] = refreshPost;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
};
export default PostFactiiiButton;
function _temp(e_0) {
  return e_0.stopPropagation();
}